import { IconLock } from '@tabler/icons-react-native';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import type { BulkListingSession } from 'app/network/listing';
import { createTheme } from 'app/shopify';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

const theme = createTheme();

export function BulkGenerationStep({
  active = true,
  isCancelling,
  isPreparing,
  photos,
  onCancel,
  onRetryFailed,
  session,
}: {
  active?: boolean;
  isCancelling: boolean;
  isPreparing: boolean;
  photos: ImagePickerAsset[];
  onCancel: () => void;
  onRetryFailed: () => void;
  session?: BulkListingSession;
}) {
  const { t } = useTranslation('new');
  const failed = session?.progress.generationFailed ?? 0;
  const images = photos.length
    ? photos.map((photo) => photo.uri)
    : (session?.drafts.map((draft) => draft.imageUrl) ?? []);
  const reducedMotion = useReducedMotion();
  const phase = useSharedValue(0);
  useEffect(() => {
    phase.value = 0;
    if (!reducedMotion && active) {
      phase.value = withRepeat(withTiming(1, { duration: 5200, easing: Easing.linear }), -1, false);
    }
    return () => cancelAnimation(phase);
  }, [active, phase, reducedMotion]);
  const frontStyle = useAnimatedStyle(() => {
    const wave = Math.sin(phase.value * Math.PI * 2);
    return { transform: [{ translateY: -5 * wave }, { rotate: `${String(-6 + 2 * wave)}deg` }] };
  });
  const backStyle = useAnimatedStyle(() => {
    const wave = Math.sin(phase.value * Math.PI * 2);
    return {
      transform: [{ translateX: 4 * wave }, { translateY: 4 * wave }, { rotate: `${String(9 - 3 * wave)}deg` }],
    };
  });

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.stack} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
          <Animated.View style={[styles.photo, styles.backPhoto, backStyle]}>
            {images[1] ? <PCImage source={{ uri: images[1] }} contentFit="cover" style={styles.image} /> : null}
          </Animated.View>
          <Animated.View style={[styles.photo, styles.frontPhoto, frontStyle]}>
            {images[0] ? <PCImage source={{ uri: images[0] }} contentFit="cover" style={styles.image} /> : null}
            <View style={styles.paperCaption}>
              <View style={styles.paperTitle} />
              <View style={styles.paperDescription} />
            </View>
          </Animated.View>
        </View>
        <Column alignItems="center" gap="spacing.general.x3">
          <Text color="text.primary" fontWeight="700" variant="headerXSmall" textAlign="center">
            {t('bulk.generation.title')}
          </Text>
          <Text color="text.secondary" variant="bodyBase" textAlign="center" style={styles.description}>
            {t('bulk.steps.generating.subtitle')}
          </Text>
        </Column>
        <Column gap="spacing.general.x3" alignItems="center">
          <View style={styles.dots} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
            {[0, 1, 2].map((index) => (
              <PreparingDot key={index} index={index} phase={phase} reducedMotion={reducedMotion} />
            ))}
          </View>
          <Text
            color="text.secondary"
            textAlign="center"
            variant="bodySmall"
            accessibilityLiveRegion="polite"
            accessibilityState={{ busy: true }}
          >
            {t(isPreparing ? 'bulk.generation.uploading' : 'bulk.generation.preparing')}
          </Text>
          {failed > 0 ? (
            <Button disabled={isPreparing || isCancelling} onPress={onRetryFailed} variant="secondary">
              {t('bulk.generation.retryFailed')}
            </Button>
          ) : null}
        </Column>
      </ScrollView>
      <View style={styles.footer}>
        <View style={styles.reassurance}>
          <IconLock color={theme.colors['text.secondary']} size={14} />
          <Text color="text.secondary" variant="bodySmall" textAlign="center" style={{ flexShrink: 1 }}>
            {t('bulk.generation.reviewHint')}
          </Text>
        </View>
        <Pressable
          accessibilityRole="button"
          disabled={isPreparing || isCancelling}
          accessibilityState={{ disabled: isPreparing || isCancelling }}
          onPress={onCancel}
          style={[styles.cancel, (isPreparing || isCancelling) && { opacity: 0.4 }]}
        >
          {isCancelling ? (
            <ActivityIndicator color={theme.colors['text.secondary']} />
          ) : (
            <Text color="text.secondary" fontWeight="600" variant="bodySmall">
              {t('bulk.common.cancel')}
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

function PreparingDot({
  index,
  phase,
  reducedMotion,
}: {
  index: number;
  phase: SharedValue<number>;
  reducedMotion: boolean;
}) {
  const animatedStyle = useAnimatedStyle(() => {
    const pulse = reducedMotion ? 0.5 : (1 + Math.sin(phase.value * Math.PI * 4 - index * 0.9)) / 2;
    return { opacity: 0.3 + pulse * 0.7, transform: [{ translateY: reducedMotion ? 0 : -3 * pulse }] };
  });
  return <Animated.View style={[styles.dot, animatedStyle]} />;
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
    gap: 28,
  },
  stack: { width: 248, height: 260, marginBottom: 8 },
  photo: {
    position: 'absolute',
    width: 184,
    height: 224,
    padding: 6,
    borderRadius: 20,
    backgroundColor: theme.colors['surface.default'],
    shadowColor: theme.colors['text.primary'],
    shadowOpacity: 0.12,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },
  backPhoto: {
    left: 46,
    top: 10,
    transform: [{ rotate: '9deg' }],
    backgroundColor: theme.colors['surface.brand.subtle'],
  },
  frontPhoto: { left: 12, top: 18, transform: [{ rotate: '-6deg' }] },
  image: { width: '100%', height: 172, borderRadius: 15, backgroundColor: theme.colors['surface.tertiary-neutral'] },
  paperCaption: { paddingHorizontal: 8, paddingTop: 12, gap: 6 },
  paperTitle: { width: '72%', height: 6, borderRadius: 3, backgroundColor: theme.colors['surface.brand.subtle'] },
  paperDescription: {
    width: '48%',
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.colors['surface.tertiary-neutral'],
  },
  dots: { flexDirection: 'row', gap: 8, height: 12, alignItems: 'center' },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: theme.colors['surface.brand.default'] },
  description: { maxWidth: 290 },
  footer: { paddingHorizontal: 24, paddingTop: 12, alignItems: 'center', gap: 8 },
  reassurance: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  cancel: { minHeight: 44, paddingHorizontal: 24, justifyContent: 'center' },
});
